import mermaid from 'mermaid';
import elkLayouts from '@mermaid-js/layout-elk';
import SvgPanZoom from 'svg-pan-zoom';

interface ProtocoloFlowchartProps {
  svgId: string;
  mermaidElem: HTMLElement;
  content: string;
  percentageStep?: number;
  panZoomOptions?: SvgPanZoom.Options;
}

function nextMultipleOf(value: number, multipleOf: number): number {
  if (value % multipleOf) {
    return value + multipleOf - (value % multipleOf);
  }

  return value + multipleOf;
}

function lastMultipleOf(value: number, multipleOf: number): number {
  if (value % multipleOf) {
    return value - (value % multipleOf);
  }

  return value - multipleOf;
}

export class ProtocoloFlowchart {
  /**
   * Elemento HTML referente a div MermaidJS onde o SVG será colocado
   */
  mermaidElem: HTMLElement;

  /**
   * Id HTML do elemento SVG que será criado/manipulado
   */
  svgId: string;

  /**
   * Texto estilo markdown usado pelo MermaidJS
   */
  content: string;

  // Valor da aproximação (em procentagem)
  percentageStep: number;

  /**
   * Configurações de pan e zoom (manilupados pela lib svg-pan-zoom)
   *
   * https://github.com/bumbu/svg-pan-zoom#how-to-use
   */
  panZoomOptions: SvgPanZoom.Options;

  /**
   * Instância do manipulador de pan e zoom, criado após chamada do método
   * `.render()`
   */
  private panZoom?: SvgPanZoom.Instance;

  /**
   * Escala inicial de zoom calculada durante a centralização do passo corrente,
   * ou seja, após chamada do método `.centerOnPassoCorrente()`
   */
  private initialZoomScale?: number;

  constructor({
    mermaidElem,
    svgId,
    content,
    percentageStep = 20,
    panZoomOptions = {},
  }: ProtocoloFlowchartProps) {
    this.mermaidElem = mermaidElem;
    this.svgId = svgId;
    this.content = content;
    this.percentageStep = percentageStep;
    this.panZoomOptions = panZoomOptions;

    this.insertSvg = this.insertSvg.bind(this);
  }

  public async render(): Promise<void> {
    const { svg, bindFunctions } = await mermaid.render(
      this.svgId,
      this.content,
      this.mermaidElem,
    );

    this.insertSvg(svg, bindFunctions);
  }

  public centerOnPassoCorrente(): void {
    const { realZoom, width, height } = this.panZoomInstance.getSizes();

    const selector = `#${this.svgId} .active`;
    const passoCorrenteElem = document.querySelector<SVGGElement>(selector);

    // Recupera o atributo transform do elemento (ex: translate(x,y)) para pegar
    // as posições x e y e assim centralizar o gráfico na posição correta
    const translateString = passoCorrenteElem
      ? passoCorrenteElem.getAttribute('transform')
      : null;

    if (translateString) {
      const [x, y] = translateString
        .replace(/[^0-9\-.,]/g, '')
        .split(',')
        .map(val => parseFloat(val));

      // Posiciona o elemento no centro do gráfico
      this.panZoomInstance.pan({
        x: -(x * realZoom) + width / 2,
        y: -(y * realZoom) + height / 2,
      });

      /**
       * Faz o zoom proporcional ao tamanho do gráfico. Quanto maior o tamanho
       * do fluxograma, maior o zoom a ser aplicado.
       *
       * Fluxogramas grandes possuem valores baixos de `realZoom`, por isso a
       * relação inversamente proporcional y = c/x
       */
      if (!this.initialZoomScale) {
        const calcScale = Math.round((0.7 / realZoom) * 100);
        this.initialZoomScale = nextMultipleOf(calcScale, 20) / 100;
      }

      this.panZoomInstance.zoom(this.initialZoomScale);
    }
  }

  public zoomIn(): void {
    const nextZoom = nextMultipleOf(this.currentZoomPercentage, 20) / 100;
    this.panZoomInstance.zoom(nextZoom);
  }

  public zoomOut(): void {
    const nextZoom = lastMultipleOf(this.currentZoomPercentage, 20) / 100;
    this.panZoomInstance.zoom(nextZoom);
  }

  private insertSvg(
    svgCode: string,
    bindFunctions?: (el: Element) => void,
  ): void {
    this.mermaidElem.innerHTML = svgCode;
    this.enablePanzoom();

    if (bindFunctions) {
      bindFunctions(this.mermaidElem);
    }
  }

  private enablePanzoom() {
    this.panZoom = SvgPanZoom(`#${this.svgId}`, this.panZoomOptions);
    this.centerOnPassoCorrente();
  }

  public get panZoomInstance(): SvgPanZoom.Instance {
    if (!this.panZoom) {
      throw new Error(
        'You should call `.render()` option before attempting to access `.panZoomInstance`.',
      );
    }

    return this.panZoom;
  }

  public get currentZoomPercentage(): number {
    return Math.round(
      parseFloat(this.panZoomInstance.getZoom().toFixed(2)) * 100,
    );
  }

  static initialize(): void {
    mermaid.registerLayoutLoaders(elkLayouts);

    mermaid.initialize({
      startOnLoad: false,
    });
  }
}

export default ProtocoloFlowchart;
